import * as React from 'react';
import { Box } from '@mui/material';
import { School } from '../../../../api/interfaces/School';
import { pushNotification } from '../../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider';
import { NotificationTypes } from '../../../../components/PinnacleSnackbarProvider/PinnacleSnackbarProvider.types';
import { handleApiError } from '../../../../utils/errorHandler';
import { Examinee, PartialEditExamineeFormRequest } from '../../../../api/interfaces/preboard/Examinee';
import { updateExaminee } from '../../../../api/services/preboard/examinee';
import UserInformation from './content/UserInformation';

interface EditExamineeDrawerContentProps {
  schools: School[];
  selectedUserId: string;
  selectedUserInfo: Examinee | null;
  onClose: any;
  onSuccess: (updatedUser: Examinee, skipClose?: boolean) => void;
  setIsSubmitting: React.Dispatch<React.SetStateAction<boolean>>;
  isSubmitting: boolean;
}

export interface EditExamineeDrawerContentHandle {
  handleSubmit: () => void;
}

const EditExamineeDrawerContent = React.forwardRef<EditExamineeDrawerContentHandle, EditExamineeDrawerContentProps>(
  ({ schools, selectedUserId, selectedUserInfo, onClose, onSuccess, setIsSubmitting, isSubmitting }, ref) => {

    React.useImperativeHandle(ref, () => ({
      handleSubmit: async () => {
        setIsSubmitting(true);

        const modifiedData = getChangedFields();
        try {
          const updateResponse = await updateExaminee(selectedUserId, modifiedData);

          onSuccess(updateResponse.data as Examinee);
          onClose();
          pushNotification({
            details: updateResponse.message,
            type: NotificationTypes.Success,
          });
        } catch (error) {
          handleApiError(error, {
            notify: (msg) => {
              pushNotification({
                details: msg,
                type: NotificationTypes.Error,
              });
            },
          });
        } finally {
          setIsSubmitting(false);
        }
      }
    }));

    // Centralized form state
    const [formData, setFormData] = React.useState<PartialEditExamineeFormRequest>({
      birthDate: selectedUserInfo?.birthDate ?? "",
      suffix: selectedUserInfo?.suffix ?? "",
      school: selectedUserInfo?.school ?? ""
    });

    // Keep a copy of the original values for comparison
    const initialDataRef = React.useRef(formData);

    const handleChange = (field: string, value: any) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
    };

    const getChangedFields = () => {
      const changed: Record<string, unknown> = {};
      for (const key in formData) {
        const typedKey = key as keyof PartialEditExamineeFormRequest;
        if (formData[typedKey] !== initialDataRef.current[typedKey]) {
          changed[typedKey] = formData[typedKey] as PartialEditExamineeFormRequest[typeof typedKey];
        }
      }
      return changed;
    };

    return (
      <Box display="flex" flexDirection="column" height="100%">
        <UserInformation
          schools={schools}
          formData={formData}
          onChange={handleChange}
          selectedUserInfo={selectedUserInfo ?? undefined}
          onSuccess={onSuccess}
          disabled={isSubmitting}
          setIsSubmitting={setIsSubmitting}
        />
      </Box>
    );
  }
);


export default EditExamineeDrawerContent;
